// React
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

// Cybs
import CybsButton from '@cybs-ui/components/lib/button';
import CybsMenu from '@cybs-ui/components/lib/menu';

import styles from './component.styles';

// Miscellaneous
import { getMenuAlignment, placeListboxInTable } from '../../utils/menuAlignment';
import { useWindowSize } from '../../utils/customHooks';

const keyCode = {
  DOWN: 'ArrowDown',
  DOWN_IE: 'Down',
  END: 'End',
  ESC_IE: 'ESC',
  ESC: 'Escape',
  HOME: 'Home',
  PAGEDOWN: 'PageDown',
  PAGEUP: 'PageUp',
  ENTER: 'Enter',
  SPACE: 'Space',
  TAB: 'Tab',
  UP: 'ArrowUp',
  UP_IE: 'Up',
  RIGHT: 'ArrowRight',
  RIGHT_IE: 'Right',
  LEFT: 'ArrowLeft',
  LEFT_IE: 'Left'
};

/**
 * @typedef {{
 *  label: string,
 *  value: string,
 *  index?: number,
 *  children?: Array<Item>,
 * }} Item
 * @param {Item} item
 * @param {{
 *  index?: (index: number) => number,
 *  children?: (children: Array<Item>) => Array<Item>,
 * }} updaters
 */
function updateItemFields(item, { index, children }) {
  if (!item) return item;
  const ret = { ...item };
  if (typeof index === 'function' && item.index !== undefined) {
    ret.index = index(item.index);
  }
  if (typeof children === 'function' && item.children !== undefined) {
    ret.children = children(item.children);
  }
  return ret;
}

/**
 * @param {Array<Item>} items
 * @param {string} parentLabel
 * @returns {Array<Item>}
 */
function injectBack(items, parentLabel = '') {
  let id = -1; // pre-incremented
  function inner(currentItems, currentParent) {
    const newItems = currentItems.map(item => updateItemFields(item, {
      children: children => inner(children, item.label)
    }));
    if (currentParent) {
      id += 1;
      return [
        {
          label: currentParent,
          value: `--back-${id}`,
          isBack: true
        },
        ...newItems
      ];
    }
    return newItems;
  }
  return inner(items, parentLabel);
}

/**
 * @param {Item} selectedItem
 * @param {number} level
 */
function injectBackIntoSelectedItem(selectedItem, level) {
  return updateItemFields(selectedItem, {
    children: children => injectBack(children, selectedItem.label),
    index: index => index + (level > 0 ? 1 : 0)
  });
}

/**
 * @param {Item} selectedItem
 * @param {number} level
 * @returns {Array<Item>}
 */
function removeBackFromChildren(selectedItem, level) {
  if (selectedItem.isBack) return [];
  return [updateItemFields(selectedItem, {
    children: c => c.flatMap(it => removeBackFromChildren(it, level + 1)),
    index: i => i - (level > 0 ? 1 : 0)
  })];
}

const MenuButton = React.forwardRef(
  (
    {
      buttonProps: buttonPropsProp,
      className,
      dataTest,
      disabled,
      isOpen,
      items: itemsProp,
      onClickOutside,
      onKeyDown,
      onSelect,
      optionsTemplate,
      position,
      selectedItem: selectedItemProp,
      width,
      tableId
    },
    ref
  ) => {
    const { width: windowWidth } = useWindowSize();
    const [showBackButton, setShowBackButton] = useState(false);

    const items = useMemo(
      () => (showBackButton ? injectBack(itemsProp) : itemsProp),
      [itemsProp, showBackButton]
    );
    const selectedItemInput = useMemo(
      () =>
        showBackButton && Array.isArray(selectedItemProp)
          ? injectBack(selectedItemProp)
          : selectedItemProp,
      [selectedItemProp, showBackButton]
    );

    // Backward Compatibility for AdvancedDropdown (deprecated)
    // TODO: Remove in next major version (6)
    const mappedItems = items
      ? items.map(item => ({
          ...item,
          label: item.label || item.value,
          value: item.value || item.label
        }))
      : [];

    const buttonProps = {
      ...MenuButton.defaultProps.buttonProps,
      ...buttonPropsProp
    };

    // Hooks
    const [isMenuOpen, toggleDropdown] = useState(isOpen);
    const [selectedItem, setSelection] = useState(
      selectedItemInput || [mappedItems[0]]
    );

    // Refs
    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const menuContainerRef = useRef(null);

    useEffect(() => {
      setSelection(_selection => {
        if (!Array.isArray(_selection)) {
          return _selection;
        }
        const newSelection = showBackButton
          ? _selection.map(injectBackIntoSelectedItem)
          : _selection.flatMap(removeBackFromChildren);

        // If focus ended on the back button when we resized, the newSelection
        //  can lose an item. Push the empty object in that case to keep the
        //  listbox open.
        if (newSelection.length < _selection.length) {
          newSelection.push({});
        }
        return newSelection;
      });
    }, [showBackButton]);

    // Focus on the listbox after opening the menu.
    useEffect(() => {
      if (isMenuOpen && menuRef?.current) {
        const menuElement = menuRef.current.getElementsByTagName('ul')[0];
        menuElement.focus();
      }
    }, [isMenuOpen]);

    // Estimate whether the listbox will fit on the screen
    const doesListboxFitOnScreen = () => {
      const bounds = buttonRef.current.getBoundingClientRect();
      // Assumes all listboxes are 3 levels deep
      // Number found by looking at the rendered listbox, not from code.
      const neededSpace = 705;
      const remainingSpace =
        position?.horizontal === 'right'
          ? bounds.right - 56 // 56px = nav aside width
          : windowWidth - bounds.left;
      return remainingSpace >= neededSpace;
    };

    useLayoutEffect(() => {
      // If in a table, use the table listbox placement util
      if (tableId && isMenuOpen) {
        placeListboxInTable(tableId);
        return;
      }
      // Fix menu alignment as page resizes (and on open) so that it doesn't go
      //  off-screen
      if (isMenuOpen && buttonRef?.current && menuContainerRef?.current) {
        const menuAlignmentCoords = getMenuAlignment(
          buttonRef.current,
          menuContainerRef.current
        );
        if (menuAlignmentCoords) {
          menuContainerRef.current.style[Object.keys(menuAlignmentCoords)[0]] =
            Object.values(menuAlignmentCoords)[0];
        }
      }
      // Show the back button when the screen crosses the threshold
      if (isMenuOpen) {
        setShowBackButton(!doesListboxFitOnScreen());
      }
    }, [isMenuOpen, windowWidth, tableId]);

    useEffect(() => {
      // Handle click outside the listbox.
      const handleOutsideClick = event => {
        if (
          buttonRef?.current?.contains(event.target) ||
          menuRef?.current?.contains(event.target)
        ) {
          return;
        }
        toggleDropdown(false);

        if (typeof onClickOutside === 'function') {
          onClickOutside(event);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }, [onClickOutside]);

    // Toggle open state of the menu.
    const handleToggleDropdown = state => {
      toggleDropdown(state);
      // Focus on the input element after closing the menu.
      if (!state) {
        if (buttonRef?.current) {
          buttonRef.current.getElementsByTagName('button')[0].focus();
        }
      }
    };

    // Click event on the button in the input.
    const handleButtonClick = event => {
      if (disabled) {
        return;
      }
      const openState = !isMenuOpen;
      handleToggleDropdown(openState);
      setSelection(
        selectedItemInput[0]?.value ? selectedItemInput : [mappedItems[0]]
      );

      if (typeof buttonProps?.onButtonClick === 'function') {
        buttonProps.onButtonClick(event, openState);
      }
    };

    // Key down event on the input element.
    const handleButtonKeyDown = event => {
      if (event) {
        // Don't flip isMenuOpen if user is only tabbing through
        const openState = event.key === keyCode.TAB ? isMenuOpen : !isMenuOpen;
        if (
          event.key === keyCode.ENTER ||
          event.key === keyCode.SPACE ||
          event.key === keyCode.DOWN
        ) {
          event.preventDefault();
          handleToggleDropdown(openState);
          setSelection(
            selectedItemInput[0]?.value ? selectedItemInput : [mappedItems[0]]
          );
        }

        if (typeof buttonProps?.onButtonKeyDown === 'function') {
          // Fully controlled keyDown event
          buttonProps.onButtonKeyDown(event, openState);
        } else if (
          // If only onButtonClick is passed in
          typeof buttonProps?.onButtonClick === 'function' &&
          (event.key === keyCode.ENTER ||
            event.key === keyCode.SPACE ||
            event.key === keyCode.DOWN)
        ) {
          buttonProps.onButtonClick(event, openState);
        }
      }
    };

    /**
     * Handle clicking/selecting the back button.
     *
     * Callers should return after calling this, because this function should
     * handle any cleanup and modifications needed for the back button to
     * function. As well, callbacks shouldn't be called to prevent the back
     * button from leaking into consumer code.
     *
     * @param {React.SyntheticEvent} event
     * @param {Item} item Back button that was just pressed.
     * @param {Array<Item | {}>} allSelectedItems Path to the back button that
     *  was just pressed, including the back button itself. This must have at
     *  least 2 items, because back buttons only appear for child items
     */
    function handleBackButton(event, item, allSelectedItems) {
      // length - 1 = the last listbox ref, i.e. the listbox containing the back
      //  button
      // length - 2 = parent listbox to the back button
      const menuElement =
        menuRef.current.getElementsByTagName('ul')[allSelectedItems.length - 2];
      menuElement.focus();
      setSelection(allSelectedItems.slice(0, -1));
    }

    // Click event on  a list item.
    const handleItemClick = (event, item, allSelectedItems) => {
      if (item.isBack) {
        handleBackButton(event, item, allSelectedItems);
        return;
      }
      // Update state no matter what
      setSelection(allSelectedItems);

      if (!item.hasNestedDropdown && (!item.children || item.children.length === 0)) {
        // selected items sent to callbacks
        const cbselected =
          showBackButton
            ? allSelectedItems?.flatMap(removeBackFromChildren)
            : allSelectedItems;
        onSelect(event, cbselected);
        handleToggleDropdown(false);
      }
    };

    // Key down event on a list item.
    const handleItemKeyDown = (event, item, selected) => {
      if (event) {
        // selected items sent to callbacks
        // back buttons are removed (if present) to avoid breakage and to
        //  make it easier for consumers, who shouldnt have to worry about
        //  whether the back button is present or not in their logic
        const cbselected =
          showBackButton
            ? selected?.flatMap(removeBackFromChildren)
            : selected;
        if (event.key === keyCode.ENTER || event.key === keyCode.SPACE) {
          if (item?.isBack) {
            handleBackButton(event, item, selected);
            return;
          }
          if (!item?.children?.length) {
            onSelect(event, cbselected);
            handleToggleDropdown(false);
          }
        } else if (event.key === keyCode.TAB || event.key === keyCode.ESC) {
          if (selected.length) {
            // Close one level, move focus to previous listbox
            const menuElement = menuRef.current.getElementsByTagName('ul')[
              selected.length > 1 ? selected.length - 1 : 0
            ];
            menuElement.focus();
          } else {
            // Close listbox and focus button
            handleToggleDropdown(false);
          }
        } else if (event.key === keyCode.LEFT) {
          const menuElement = menuRef.current.getElementsByTagName('ul')[
            selected.length > 1 ? selected.length - 1 : 0
          ];
          menuElement.focus();
        }

        if (selected) {
          setSelection(selected);
        }
        if (typeof onKeyDown === 'function') {
          onKeyDown(event, cbselected);
        }
      }
    };

    return (
      <div
        css={theme =>
          styles(theme, position, isMenuOpen, buttonProps.variant, disabled)
            .menuContainer
        }
        className={className}
        data-test={`${dataTest}-cybsMenuButton`}
        ref={ref}
      >
        <div ref={buttonRef}>
          <CybsButton
            {...buttonProps}
            aria-expanded={isMenuOpen}
            aria-haspopup
            colorScheme={buttonProps.colorScheme}
            css={theme =>
              styles(theme, position, isMenuOpen, buttonProps.variant, disabled)
                .buttonContainer
            }
            dataTest={dataTest}
            disabled={disabled}
            i18n={{
              label: buttonProps?.i18n?.label || buttonProps?.label,
              tooltipLabel:
                buttonProps?.i18n?.tooltipLabel || buttonProps.tooltipLabel
            }}
            iconClass={
              buttonProps.iconClass ||
              (buttonProps.variant === 'split' && isMenuOpen
                ? 'arrow-collapse'
                : 'arrow-expand')
            }
            iconType={buttonProps.iconType}
            triggerOnKeyUp={false} // Prevents the menu buttons from being triggered on key up
            onClick={handleButtonClick}
            onKeyDown={handleButtonKeyDown}
            variant={buttonProps.variant}
          />
        </div>
        {isMenuOpen && mappedItems.length ? (
          <div ref={menuContainerRef} css={theme => styles(theme, position).menuButtonWrapper}>
            <CybsMenu
              dataTest={dataTest}
              onListKeyDown={handleItemKeyDown}
              onListSelect={handleItemClick}
              position={position}
              ref={menuRef}
              selectedItems={selectedItem}
              show={isMenuOpen}
              type="nested"
              width={width}
              optionsTemplate={optionsTemplate}
              useLowResolutionStyles={showBackButton}
            >
              {mappedItems}
            </CybsMenu>
          </div>
        ) : null}
      </div>
    );
  }
);

MenuButton.propTypes = {
  /**
   * Props to applied to the input element.
   */
  buttonProps: PropTypes.shape({
    /**
     * Color scheme of button
     */
    colorScheme: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),

    /**
     * Name for the button icon.
     */
    iconClass: PropTypes.string,

    /**
     * Icon type
     */
    iconType: PropTypes.oneOf([
      'dark',
      'dark-outline',
      'dark-tiny',
      'light',
      'light-fill',
      'light-outline',
      'light-tiny'
    ]),
    /**
     * Strings for the component. Maybe i18n object or passed in separately as props.
     */
    i18n: PropTypes.shape({
      label: PropTypes.string,
      tooltipLabel: PropTypes.string
    }),

    /**
     * @ignore Label of the button. Use i18n instead.
     */
    label: PropTypes.string,

    /**
     *Callback fired when the button element is clicked.
     */
    onButtonClick: PropTypes.func,

    /**
     *Callback fired on key down on the the button element.
     */
    onButtonKeyDown: PropTypes.func,

    /**
     * Label for the tooltip.
     */
    tooltipLabel: PropTypes.string.isRequired,

    /**
     * Category of button
     */
    variant: PropTypes.oneOf(['split', 'text', 'text-icon'])
  }),

  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   *If `true`, the combobox  is not interactive.
   */
  disabled: PropTypes.bool,

  /**
   *If `true`, the initial state of the menu is open.
   */
  isOpen: PropTypes.bool,

  /**
   * List of items in the listbox.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,

  /**
   * Callback fired on click outside of the menu or button element.
   */
  onClickOutside: PropTypes.func,

  /**
   * Callback fired on key down in the listbox.
   */
  onKeyDown: PropTypes.func,

  /**
   * Callback fired when an item is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Different positions for the menu around the input element.
   */
  position: PropTypes.shape({
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
    vertical: PropTypes.oneOf(['bottom', 'top'])
  }),

  /**
   * Selected item(s).
   */
  selectedItem: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    )
  ]),

  /**
   * @ignore
   */
  tableId: PropTypes.string,

  /**
   * Width of menu box.
   */
  width: PropTypes.string,
  /**
   * optionsTemplate to render multiple attributes in the listbox
   */
  optionsTemplate: PropTypes.shape({
    optionValues: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.string.isRequired })
    ),
    renderOption: PropTypes.func.isRequired,
    renderLabel: PropTypes.func.isRequired,
    validationTemplate: PropTypes.func.isRequired
  }),

  /**
   * Class name applied to Menu Button.
   */
  className: PropTypes.string
};

MenuButton.defaultProps = {
  buttonProps: PropTypes.shape({
    colorScheme: 'secondary',
    iconClass: 'arrow-collapse',
    iconType: 'light',
    i18n: {},
    label: '',
    tooltipLabel: '',
    variant: 'split'
  }),
  disabled: false,
  isOpen: false,
  onClickOutside: () => {},
  onKeyDown: () => {},
  onSelect: () => {},
  position: {
    horizontal: 'left',
    vertical: 'bottom'
  },
  selectedItem: [{}],
  tableId: '',
  width: '',
  optionsTemplate: undefined,
  className: ''
};

MenuButton.displayName = 'MenuButton';

export default MenuButton;
